import { ArrowClockwiseIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";

import { apiRequest } from "../api/client";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { LoadingState } from "../components/LoadingState";
import type {
  FunnelEvent,
  MarketingEvent,
  MarketingMetric,
  MarketingSummary,
  TrafficVisit,
  TrafficVisitor,
} from "../types";

export function MarketingPage() {
  const [events, setEvents] = useState<MarketingEvent[]>([]);
  const [visits, setVisits] = useState<TrafficVisit[]>([]);
  const [visitors, setVisitors] = useState<TrafficVisitor[]>([]);
  const [funnelEvents, setFunnelEvents] = useState<FunnelEvent[]>([]);
  const [summary, setSummary] = useState<MarketingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [eventType, setEventType] = useState("");
  const [period, setPeriod] = useState<"7d" | "30d" | "all">("30d");
  const [selectedVisitId, setSelectedVisitId] = useState("");
  const [loadedAt, setLoadedAt] = useState(0);

  const eventTypes = useMemo(
    () => [...new Set(events.map((event) => event.event_type))].sort(),
    [events],
  );
  const periodEvents = useMemo(() => {
    if (period === "all") return events;
    const days = period === "7d" ? 7 : 30;
    const since =
      (loadedAt || Number.MAX_SAFE_INTEGER) - days * 24 * 60 * 60 * 1000;
    return events.filter(
      (event) => new Date(event.created_at).getTime() >= since,
    );
  }, [events, loadedAt, period]);
  const filteredEvents = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return periodEvents.filter((event) => {
      if (eventType && event.event_type !== eventType) return false;
      if (!normalized) return true;
      return [
        event.event_type,
        event.path,
        event.source,
        event.locale,
        utmLabel(event),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalized);
    });
  }, [eventType, periodEvents, query]);
  const periodMetrics = useMemo(() => {
    const views = periodEvents.filter(
      (event) => event.event_type === "page_view",
    ).length;
    const leads = periodEvents.filter(
      (event) => event.event_type === "lead_submission",
    ).length;
    const conversion = views ? Math.round((leads / views) * 1000) / 10 : 0;
    return { views, leads, conversion };
  }, [periodEvents]);
  const selectedVisit =
    visits.find((visit) => visit.id === selectedVisitId) ?? visits[0] ?? null;
  const selectedVisitEvents = selectedVisit
    ? events.filter(
        (event) =>
          event.visit_id === selectedVisit.id ||
          event.session_id === selectedVisit.session_id,
      )
    : [];

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [nextSummary, nextEvents, nextVisits, nextVisitors, nextFunnel] =
        await Promise.all([
          apiRequest<MarketingSummary>("/api/v1/admin/marketing/summary"),
          apiRequest<MarketingEvent[]>("/api/v1/admin/marketing/events"),
          apiRequest<TrafficVisit[]>("/api/v1/admin/marketing/visits"),
          apiRequest<TrafficVisitor[]>("/api/v1/admin/marketing/visitors"),
          apiRequest<FunnelEvent[]>("/api/v1/admin/marketing/funnel-events"),
        ]);
      setSummary(nextSummary);
      setEvents(nextEvents);
      setVisits(nextVisits);
      setVisitors(nextVisitors);
      setFunnelEvents(nextFunnel);
      setLoadedAt(Date.now());
    } catch {
      setError("Не удалось загрузить аналитику");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Маркетинг</span>
          <h1>Аналитика трафика</h1>
          <p>
            Откуда приходят люди, какие UTM-метки работают, какие страницы
            собирают касания и заявки.
          </p>
        </div>
        <Button onClick={() => void load()} tone="secondary">
          <ArrowClockwiseIcon size={16} /> Обновить
        </Button>
      </header>

      {error ? <div className="notice notice--error">{error}</div> : null}
      {loading ? (
        <LoadingState rows={7} />
      ) : summary ? (
        <>
          <section className="metrics-strip analytics-metrics">
            <div>
              <span>Всего событий</span>
              <strong>{summary.total_events}</strong>
            </div>
            <div>
              <span>Посетители</span>
              <strong>{summary.visitors}</strong>
            </div>
            <div>
              <span>Визиты</span>
              <strong>{summary.visits}</strong>
            </div>
            <div>
              <span>Просмотры страниц</span>
              <strong>{summary.page_views}</strong>
            </div>
            <div>
              <span>Заявки</span>
              <strong>{summary.leads}</strong>
            </div>
            <div>
              <span>Конверт. визиты</span>
              <strong>{summary.converted_visits}</strong>
            </div>
          </section>

          <div className="table-toolbar analytics-period">
            <label>
              <span>Период журнала</span>
              <select
                onChange={(event) =>
                  setPeriod(event.target.value as "7d" | "30d" | "all")
                }
                value={period}
              >
                <option value="7d">7 дней</option>
                <option value="30d">30 дней</option>
                <option value="all">Все события</option>
              </select>
            </label>
            <strong>
              {periodMetrics.views} просмотров · {periodMetrics.leads} заявок
            </strong>
          </div>

          <section className="analytics-grid">
            <MetricList title="Источники" items={summary.top_sources} />
            <MetricList title="Страницы" items={summary.top_paths} />
            <MetricList title="UTM source" items={summary.utm_sources} />
            <MetricList title="UTM medium" items={summary.utm_mediums} />
            <MetricList title="UTM campaign" items={summary.utm_campaigns} />
            <MetricList title="UTM term" items={summary.utm_terms} />
            <MetricList title="UTM content" items={summary.utm_contents} />
            <MetricList title="Локали" items={summary.top_locales} />
            <MetricList title="Устройства" items={summary.top_devices} />
          </section>

          <section className="event-log traffic-log">
            <div className="section-heading">
              <div>
                <span className="eyebrow">Сессии</span>
                <h2>Последние визиты</h2>
              </div>
              <strong>
                {periodMetrics.views} просмотров · {periodMetrics.leads} заявок
                · {periodMetrics.conversion}% периода
              </strong>
            </div>
            <div className="event-log__head event-log__head--visits">
              <span>Landing</span>
              <span>Источник</span>
              <span>UTM / устройство</span>
              <span>События</span>
              <span>Статус</span>
            </div>
            {visits.slice(0, 20).map((visit) => (
              <button
                className={`event-log__row event-log__row--visits ${
                  selectedVisit?.id === visit.id ? "is-active" : ""
                }`}
                key={visit.id}
                onClick={() => setSelectedVisitId(visit.id)}
                type="button"
              >
                <span>{visit.landing_path || "—"}</span>
                <span>{visit.source || visit.referrer || "direct"}</span>
                <span>
                  {utmText(visit.utm_json)}
                  {visit.device_type ? ` · ${visit.device_type}` : ""}
                </span>
                <span>
                  {visit.page_view_count} views · {visit.event_count} events
                </span>
                <strong>{visit.converted ? "converted" : "active"}</strong>
              </button>
            ))}
            {!visits.length ? <p>Визитов пока нет.</p> : null}
          </section>

          {selectedVisit ? (
            <section className="analytics-card visit-detail">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Разбор визита</span>
                  <h2>
                    {selectedVisit.landing_path ||
                      selectedVisit.landing_url ||
                      "visit"}
                  </h2>
                </div>
                <strong>
                  {selectedVisit.converted ? "converted" : "not converted"}
                </strong>
              </div>
              <dl className="lead-fields visit-fields">
                <div>
                  <dt>Источник</dt>
                  <dd>
                    {selectedVisit.source || selectedVisit.referrer || "direct"}
                  </dd>
                </div>
                <div>
                  <dt>UTM</dt>
                  <dd>{utmText(selectedVisit.utm_json)}</dd>
                </div>
                <div>
                  <dt>Visitor / session</dt>
                  <dd>
                    {selectedVisit.visitor_id} · {selectedVisit.session_id}
                  </dd>
                </div>
                <div>
                  <dt>Устройство</dt>
                  <dd>{selectedVisit.device_type || "—"}</dd>
                </div>
                <div>
                  <dt>Старт</dt>
                  <dd>{new Date(selectedVisit.started_at).toLocaleString()}</dd>
                </div>
                <div>
                  <dt>Последнее касание</dt>
                  <dd>
                    {new Date(selectedVisit.last_seen_at).toLocaleString()}
                  </dd>
                </div>
              </dl>
              <div className="visit-timeline">
                {selectedVisitEvents.slice(0, 8).map((event) => (
                  <div key={event.id}>
                    <strong>{event.event_type}</strong>
                    <span>{event.path || "—"}</span>
                    <small>{new Date(event.created_at).toLocaleString()}</small>
                  </div>
                ))}
                {!selectedVisitEvents.length ? (
                  <p>Событий визита пока нет.</p>
                ) : null}
              </div>
            </section>
          ) : null}

          <section className="analytics-grid">
            <article className="analytics-card">
              <h2>Последние посетители</h2>
              <div className="metric-list metric-list--wide">
                {visitors.slice(0, 8).map((visitor) => (
                  <div key={visitor.id}>
                    <span>
                      {visitor.external_id}
                      <small>{visitor.last_source || "direct"}</small>
                    </span>
                    <strong>
                      {visitor.total_visits}/{visitor.total_conversions}
                    </strong>
                  </div>
                ))}
              </div>
            </article>
            <article className="analytics-card">
              <h2>Воронка форм</h2>
              <div className="metric-list metric-list--wide">
                {funnelEvents.slice(0, 8).map((event) => (
                  <div key={event.id}>
                    <span>
                      {event.event_type}
                      <small>{event.form_id || event.path || "form"}</small>
                    </span>
                    <strong>
                      {new Date(event.created_at).toLocaleTimeString()}
                    </strong>
                  </div>
                ))}
              </div>
            </article>
          </section>

          {events.length ? (
            <section className="event-log">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Журнал</span>
                  <h2>Последние касания</h2>
                </div>
                <Button
                  onClick={() =>
                    downloadCsv(
                      "unicore-marketing-events.csv",
                      filteredEvents.map((event) => ({
                        event_type: event.event_type,
                        path: event.path ?? "",
                        source: event.source ?? "",
                        visitor_id: event.visitor_id ?? "",
                        visit_id: event.visit_id ?? "",
                        session_id: event.session_id ?? "",
                        locale: event.locale ?? "",
                        utm: utmLabel(event),
                        created_at: event.created_at,
                      })),
                    )
                  }
                  tone="secondary"
                >
                  CSV
                </Button>
              </div>
              <div className="table-toolbar">
                <label>
                  <span>Поиск</span>
                  <input
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="source, path, campaign"
                    value={query}
                  />
                </label>
                <label>
                  <span>Тип события</span>
                  <select
                    onChange={(event) => setEventType(event.target.value)}
                    value={eventType}
                  >
                    <option value="">Все события</option>
                    {eventTypes.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </label>
                <strong>{filteredEvents.length} строк</strong>
              </div>
              <div className="event-log__head">
                <span>Событие</span>
                <span>Страница</span>
                <span>Источник / UTM</span>
                <span>Время</span>
              </div>
              {filteredEvents.map((event) => (
                <div className="event-log__row" key={event.id}>
                  <strong>{event.event_type}</strong>
                  <span>{event.path || "—"}</span>
                  <span>{event.source || utmLabel(event) || "direct"}</span>
                  <span>{new Date(event.created_at).toLocaleString()}</span>
                </div>
              ))}
            </section>
          ) : null}
        </>
      ) : (
        <EmptyState
          title="Событий пока нет"
          description="Откройте публичный сайт локально, и page_view появится здесь."
        />
      )}
    </div>
  );
}

function MetricList({
  title,
  items,
}: {
  title: string;
  items: MarketingMetric[];
}) {
  return (
    <article className="analytics-card">
      <h2>{title}</h2>
      {items.length ? (
        <div className="metric-list">
          {items.map((item) => (
            <div key={item.key}>
              <span>{item.key}</span>
              <strong>{item.count}</strong>
            </div>
          ))}
        </div>
      ) : (
        <p>Пока нет данных.</p>
      )}
    </article>
  );
}

function utmLabel(event: MarketingEvent) {
  const utm = event.payload_json.utm;
  if (!utm || typeof utm !== "object") return "";
  const source = "utm_source" in utm ? String(utm.utm_source ?? "") : "";
  const medium = "utm_medium" in utm ? String(utm.utm_medium ?? "") : "";
  const campaign = "utm_campaign" in utm ? String(utm.utm_campaign ?? "") : "";
  const term = "utm_term" in utm ? String(utm.utm_term ?? "") : "";
  const content = "utm_content" in utm ? String(utm.utm_content ?? "") : "";
  return [source, medium, campaign, term, content].filter(Boolean).join(" / ");
}

function utmText(value: Record<string, unknown>) {
  const entries = Object.entries(value).filter(([, item]) => Boolean(item));
  if (!entries.length) return "—";
  return entries.map(([key, item]) => `${key}: ${String(item)}`).join(" · ");
}

function downloadCsv(filename: string, rows: Record<string, string>[]) {
  const headers = Object.keys(rows[0] ?? {});
  const escape = (value: string) => `"${value.replaceAll('"', '""')}"`;
  const csv = [
    headers.join(","),
    ...rows.map((row) =>
      headers.map((key) => escape(row[key] ?? "")).join(","),
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
